import { SetStateAction, useEffect, useRef, useState, Dispatch } from "react";

import { FormOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { clsx } from "clsx";

import { RVLoadingTitle } from "../RVLoadingTitle/RVLoadingTitle";

import styles from "./RVEditable.module.scss";

const ESCAPE_KEY = 27;

export type RVEditableProps = {
    /** Current controlled value. Kept in sync with the internal edit state. */
    value?: string;
    /** Called with the new value when the user commits an edit (Enter or blur). */
    onChange?: (value: string) => void;
    /** Initial value used before any external `value` is provided. */
    defaultValue?: string;
    /** When true, the component mounts directly in edit mode. */
    startWithEditView?: boolean;
    /** When true, renders a loading placeholder instead of the preview/input. */
    loading?: boolean;
    /** Extra class applied to the underlying edit input. */
    inputClassName?: string;
    /** Element type used to render the preview value (defaults to `span`). */
    previewAs?: React.ElementType<any>;
    /** Optional value shown in preview mode when it should differ from `value`. */
    previewValue?: string;
    /** Maximum number of characters allowed in the input. */
    maxLength?: number;
    /** When true, preview mode cannot be edited. */
    disabled?: boolean;
    /** When true, edit mode is only triggered by clicking the edit icon, not the text. */
    iconOnlyEditTrigger?: boolean;
    /** When true, the edit input spans the full width of the container (default is 50%). */
    fullWidthEdit?: boolean;
};

/**
 * Inline-editable text field that toggles between a preview and an editable input.
 *
 * Clicking the preview (or the edit icon when `iconOnlyEditTrigger` is set) switches
 * to an input; committing via Enter or blur calls `onChange` with the new value.
 *
 * @example
 * <RVEditable value={title} onChange={setTitle} />
 */
export function RVEditable({
    value,
    onChange,
    defaultValue = "",
    startWithEditView = false,
    loading = false,
    inputClassName,
    previewAs,
    previewValue,
    maxLength = 50,
    disabled = false,
    iconOnlyEditTrigger = false,
    fullWidthEdit = false,
}: RVEditableProps): JSX.Element {
    const [isEditing, setEditing] = useState<boolean>(startWithEditView);
    const [innerValue, setInnerValue] = useState<string>(defaultValue);
    const [iconHovered, setIconHovered] = useState<boolean>(false);

    function handleSubmit(value: string) {
        onChange?.(value);
    }

    useEffect(() => {
        setInnerValue(value || "");
    }, [value]);

    // the edit should not persist when user clicks out of the box
    // the problem is that clicking on save icon is seen as blur event by div wrapper
    function onBlur() {
        if (!iconHovered) {
            setEditing(false);
            setInnerValue(value || "");
        }
    }

    return loading ? (
        <RVLoadingTitle />
    ) : (
        <div className={clsx(styles.editable, fullWidthEdit && styles.fullWidthEdit)} onBlur={onBlur}>
            {isEditing ? (
                <RVEditableInput
                    value={innerValue}
                    initialValue={value || ""}
                    onChange={setInnerValue}
                    setValue={handleSubmit}
                    setEditing={setEditing}
                    setIconHovered={setIconHovered}
                    inputClassName={inputClassName}
                    maxLength={maxLength}
                />
            ) : (
                <RVEditablePreview
                    value={previewValue ?? innerValue}
                    setEditing={setEditing}
                    as={previewAs}
                    disabled={disabled}
                    iconOnlyEditTrigger={iconOnlyEditTrigger}
                />
            )}
        </div>
    );
}

type RVEditablePreviewProps = {
    /** Value rendered in preview mode. */
    value: string;
    /** Setter used to enter edit mode. */
    setEditing: Dispatch<SetStateAction<boolean>>;
    /** Element type used to render the value (defaults to `span`). */
    as?: React.ElementType<any>;
    /** When true, editing is disabled. */
    disabled?: boolean;
    /** When true, only the edit icon triggers edit mode. */
    iconOnlyEditTrigger?: boolean;
};

function RVEditablePreview({
    value,
    setEditing,
    as,
    disabled,
    iconOnlyEditTrigger,
}: RVEditablePreviewProps): JSX.Element {
    const Component = as || "span";

    function beginEdit(event: React.MouseEvent | React.KeyboardEvent) {
        if (disabled) {
            return;
        }
        // Always stop propagation when entering edit mode so ancestor click targets
        // (e.g. selectable session rows) do not handle the same gesture as a selection.
        event.stopPropagation();
        setEditing(true);
    }

    function handleIconKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            beginEdit(event);
        }
    }

    // Keep the icon non-interactive by default to preserve existing tabindex behavior.
    const iconClickHandler = iconOnlyEditTrigger ? (beginEdit as React.MouseEventHandler) : undefined;

    return (
        <div
            className={clsx("flex-row align-center", styles.previewWrapper)}
            onClick={iconOnlyEditTrigger ? undefined : (beginEdit as React.MouseEventHandler)}
        >
            <Component>{value}</Component>
            {iconOnlyEditTrigger ? (
                <span
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-label="Edit"
                    aria-disabled={disabled}
                    className={styles.previewIcon}
                    onClick={iconClickHandler}
                    onKeyDown={handleIconKeyDown}
                >
                    <FormOutlined />
                </span>
            ) : (
                <FormOutlined className={styles.previewIcon} />
            )}
        </div>
    );
}

type RVEditableInputProps = {
    /** Current input value. */
    value: string;
    /** Value to restore when the edit is cancelled via Escape. */
    initialValue: string;
    /** Setter used to leave edit mode. */
    setEditing: Dispatch<SetStateAction<boolean>>;
    /** Called on every keystroke with the new value. */
    onChange: (newValue: string) => void;
    /** Commits the value to the parent. */
    setValue: (newValue: string) => void;
    /** Tracks whether the save icon is hovered to guard against blur. */
    setIconHovered: (hovered: boolean) => void;
    /** Extra class applied to the input. */
    inputClassName?: string;
    /** Maximum number of characters allowed. */
    maxLength?: number;
};

function RVEditableInput({
    value,
    initialValue,
    setEditing,
    onChange,
    setValue,
    setIconHovered,
    inputClassName,
    maxLength,
}: RVEditableInputProps): JSX.Element {
    const inputRef = useRef<any>(null);

    useEffect(() => {
        inputRef.current.focus();
    }, []);

    function updateValue() {
        setValue(value);
        setEditing(false);
        setIconHovered(false);
    }

    function handleOnKeyPress(e: React.KeyboardEvent) {
        if (e.keyCode == ESCAPE_KEY) {
            onChange(initialValue);
            setEditing(false);
            setIconHovered(false);
        }
    }

    return (
        <div className={clsx(styles.editWrapper)}>
            <Input
                ref={inputRef}
                value={value}
                className={clsx(styles.editableInput, inputClassName)}
                bordered={true}
                maxLength={maxLength}
                onChange={(e) => onChange(e.target.value)}
                onPressEnter={updateValue}
                onBlur={updateValue}
                onKeyDown={handleOnKeyPress}
            />
        </div>
    );
}
